/** Targets and plan - the transition plan, requirement **EXP-20** (ADR-0212: a place of the menu with
 * three tabs here - **Targets**, **Recommendations**, **Initiatives** - and the climate risks as the
 * fourth, at `/risks`).
 *
 * > *"Separate baseline, assumptions, delivery actions, evidence, measurement and decisions."*
 *
 * **The separation is the requirement, so it survives the redesign.** Each tab is a table page - a
 * title, tiles, one filter line and one grid - and the parts that were six panels on one screen are
 * behind buttons that open a drawer, none standing in for another:
 *
 * - **Targets**: the commitment (a grid of target versions), and in drawers the pathway, what the
 *   assumptions are, what they would mean (*modelled*, never the inventory) and the decision log
 *   (append-only, and the log says so). A target is committed to, or revised, from the **Add target**
 *   drawer; a revision is a new version and the history is readable. **A baseline that differs from the
 *   last version comes back from the server waiting for an assessment, with a task raised** (ACT-01,
 *   ADR-0108), and the first thing the page prints is that and the one act that clears it.
 * - **Initiatives**: **expected and achieved side by side and never added** (EXP-37), with the
 *   server's overlap findings in place of a portfolio total (ACT-02).
 * - **Recommendations**: EmitIQ's analysis from your data, read from `GET /plan/recommendations`
 *   (`TransitionRecommendations.tsx`), see below.
 *
 * ## The numbers this place will not show
 *
 * **A portfolio total.** Summing overlapping initiatives as independent reductions is ACT-02's
 * defect; the server reports the overlaps as *findings* and the page counts them.
 *
 * **Progress against the target.** That is a question about measured emissions with their own
 * boundary, allocation and receipts, and joining the two casually produces a progress figure with no
 * receipt behind it - rule 3's whole subject. It is a real feature and it is **not built**; the
 * Commitment tile's info control says that, and also that this is the plan **as it stands today**
 * (ADR-0048 item 4: nothing records which version a report was issued against).
 *
 * ## Recommendations
 *
 * ADR-0212 decision 7 allows only what is computed from the company's own data and labelled **EmitIQ's
 * analysis from your data** (the largest sources, missing data, estimates to replace with readings,
 * sites or categories with no data) and refuses any saving, cost or pathway the company did not enter
 * or a verified source did not give. `GET /plan/recommendations` is that read; the tab prints it and
 * computes nothing.
 */

import { Leaf, Plus } from "@phosphor-icons/react";
import { useState } from "react";

import { useMe } from "../api/auth";
import type { PlanInitiative } from "../api/queries";
import { useAddInitiative, usePlan, useRecordDecision, useScenarios } from "../api/queries";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import {
  ActionButton,
  Drawer,
  EmptyAction,
  EmptyState,
  FilterLine,
  GridTable,
  HowDrawer,
  EMPTY_FILTERS,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
} from "../components/ui";
import type { FilterState, GridColumn } from "../components/ui";
import { factorValue, shortDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { useGridSort } from "./gridSort";
import { RecordLink } from "./RecordLink";
import { ScreenFrame } from "./ScreenFrame";
import { TransitionPathway } from "./TransitionPathway";
import { InitiativeBars } from "./TransitionPlanExtras";
import { RecommendationsTab } from "./TransitionRecommendations";
import {
  CommitTarget,
  NeedsAssessment,
  RecordAssumption,
  Scenarios,
  TargetHistory,
} from "./TransitionTarget";
import styles from "./Screen.module.css";
import own from "./Transition.module.css";
import { useScreen } from "./useScreen";
import { useUrlTab } from "./useUrlTab";

type PlanTarget = NonNullable<ReturnType<typeof usePlan>["data"]>["targets"][number];

const TABS = [
  { key: "targets", label: "Targets" },
  { key: "recommendations", label: "Recommendations" },
  { key: "initiatives", label: "Initiatives" },
] as const;

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** Mirrored from `plan.py`. Committing and deciding are statements about the company; delivering is
 * the work, and a Contributor does the work. */
const MAY_COMMIT: readonly string[] = ["admin", "approver"];
const MAY_DELIVER: readonly string[] = ["admin", "approver", "contributor"];

type Drawn = "target" | "add-target" | "assumptions" | "decisions" | "add-initiative" | null;

function TargetsTab() {
  const { data: me } = useMe();
  const { data: plan, error: planFailed, refetch: retryPlan, isPending } = usePlan();
  const [drawn, setDrawn] = useState<Drawn>(null);
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTERS);

  const mayCommit = me ? MAY_COMMIT.includes(me.role) : undefined;
  const target = plan?.targets[0];

  // **The scenario drawn on the pathway.** The newest assumptions version until a reader chooses
  // another (or none): choosing moves the line live, and recording a new version, which refreshes the
  // same read, brings it in. `undefined` is "not chosen yet", `null` is "none, by choice".
  const scenarioRead = useScenarios(target?.key, target !== undefined);
  const [chosenVersion, setChosenVersion] = useState<number | null | undefined>(undefined);
  const drawnVersions = (scenarioRead.data?.projections ?? [])
    .filter((projection) => !projection.refusal && projection.emissions !== null)
    .map((projection) => projection.assumption_version);
  const newest = drawnVersions.length > 0 ? Math.max(...drawnVersions) : null;
  const shownVersion =
    chosenVersion === undefined
      ? newest
      : chosenVersion !== null && !drawnVersions.includes(chosenVersion)
        ? newest
        : chosenVersion;

  const rows = (plan?.targets ?? []).filter((row) => {
    const needle = filter.search.trim().toLowerCase();
    return !needle || row.sentence.toLowerCase().includes(needle);
  });
  const { sorted, sort, setSort } = useGridSort(rows, {
    sentence: (row) => row.sentence,
    horizon: (row) => row.horizon_year,
    version: (row) => row.version,
  });
  const standing = target?.standing === "needs_reassessment" ? "needs_reassessment" : "in_force";

  const columns: GridColumn<PlanTarget>[] = [
    {
      id: "sentence",
      header: "Target",
      sortable: true,
      // A sentence: it wraps inside its column (it ran across the five number columns beside it).
      minWidth: "240px",
      wrap: true,
      value: (row) => row.sentence,
      cell: (row) => (
        <button type="button" className={styles.fixLink} data-testid="commitment" onClick={() => setDrawn("target")}>
          {row.sentence}
        </button>
      ),
    },
    { id: "baseline", header: "Baseline", numeric: true, width: "128px", cell: (row) => `${factorValue(row.baseline_amount)} ${row.baseline_unit}` },
    { id: "level", header: "Committed level", numeric: true, width: "148px", cell: (row) => `${factorValue(row.implied_target)} ${row.baseline_unit}` },
    { id: "horizon", header: "Horizon", sortable: true, numeric: true, width: "96px", cell: (row) => String(row.horizon_year) },
    {
      id: "version",
      header: "Version",
      sortable: true,
      numeric: true,
      width: "112px",
      cell: (row) => `${String(row.version)}${row.because ? ` · ${row.because}` : ""}`,
    },
    { id: "validated", header: "Validated", width: "196px", cell: (row) => row.validated_by ?? "Not externally validated" },
  ];

  if (planFailed) {
    return (
      <TablePage
        icon={Leaf}
        title="Targets and plan"
        label="Targets"
        state={<ReadFailed of="The transition plan" error={planFailed} onRetry={() => void retryPlan()} />}
      />
    );
  }

  return (
    <>
      <TablePage
        icon={Leaf}
        title="Targets and plan"
        label="Targets"
        actions={
          <span className={cls(own.actions)}>
            <ActionButton onClick={() => setDrawn("assumptions")} data-testid="open-assumptions">
              Assumptions
            </ActionButton>
            <ActionButton onClick={() => setDrawn("decisions")} data-testid="open-decisions">
              Decisions
            </ActionButton>
            {mayCommit === false ? (
              <span data-testid="not-permitted-target">
                <StatusChip tone="neutral" size="sm">
                  An Admin or an Approver commits to a target
                </StatusChip>
              </span>
            ) : (
              <ActionButton variant="primary" onClick={() => setDrawn("add-target")} data-testid="open-add-target">
                <Plus size={14} weight="bold" aria-hidden="true" /> {target ? "Revise target" : "Add target"}
              </ActionButton>
            )}
          </span>
        }
        band={
          /* **A restated baseline is waiting for a person** (ACT-01, ADR-0108), and that is the first
             thing on the page. The server wrote the version `needs_reassessment` and raised a task;
             this prints that and offers the one act that clears it. It shows no size and says nothing
             about whether the change matters. */
          target && standing === "needs_reassessment" ? (
            <NeedsAssessment target={target} mayCommit={mayCommit} reload={() => void retryPlan()} />
          ) : undefined
        }
        tiles={
          <TileStrip label="The plan at a glance">
            <Tile
              value={target ? factorValue(target.baseline_amount) : "-"}
              unit={target ? ` ${target.baseline_unit}` : undefined}
              label="Baseline"
              loading={isPending}
              testId="plan-baseline"
            />
            <Tile
              value={target ? factorValue(target.implied_target) : "-"}
              unit={target ? ` ${target.baseline_unit}` : undefined}
              label="Committed level"
              loading={isPending}
              testId="plan-committed"
            />
            <Tile
              value={!target ? "Not made" : standing === "needs_reassessment" ? "Awaiting assessment" : "In force"}
              label="The commitment"
              infoKey="targets.commitment"
              loading={isPending}
              testId="plan-standing"
            />
            <Tile
              value={!plan ? "-" : plan.assumptions_version === null ? "None" : `v${String(plan.assumptions_version)}`}
              label="Assumptions"
              loading={isPending}
              testId="plan-assumptions"
            />
            <Tile value={plan ? String(plan.initiatives.length) : "-"} label="Delivery actions" loading={isPending}
              testId="plan-actions" />
          </TileStrip>
        }
        filters={
          plan && plan.targets.length === 0 ? undefined : (
            <FilterLine
              filters={[]}
              state={filter}
              onChange={setFilter}
              resultCount={plan ? { shown: rows.length, total: plan.targets.length, noun: "targets" } : undefined}
            />
          )
        }
        table={
          <>
            <GridTable
              caption="The company's targets"
              columns={columns}
              rows={sorted}
              rowKey={(row) => row.id}
              rowNumbers={false}
              loading={isPending}
              sort={sort}
              onSortChange={setSort}
              empty={
                <EmptyState
                  icon={Leaf}
                  title="No target yet"
                  compact
                  testId="no-target"
                  action={
                    <EmptyAction
                      label="Add target"
                      allowed={mayCommit}
                      reason="An Admin or Approver commits to a target."
                      onClick={() => setDrawn("add-target")}
                      testId="no-target-add"
                    />
                  }
                >
                  Commit to a reduction, and the plan tracks it.
                </EmptyState>
              }
              testId="targets"
            />
          </>
        }
      />

      <Drawer open={drawn === "target"} onClose={() => setDrawn(null)} title="The commitment" width="lg">
        {target ? (
          <div className={cls(own.stack)}>
            <p className={cls(own.sentence)}>{target.sentence}</p>
            <TargetHistory targetKey={target.key} />
            <TransitionPathway target={target} shown={shownVersion} onShow={setChosenVersion} />
            <Scenarios targetKey={target.key} shown={shownVersion} onShow={setChosenVersion} />
          </div>
        ) : null}
      </Drawer>

      <Drawer
        open={drawn === "add-target"}
        onClose={() => setDrawn(null)}
        title={target ? "Revise the target" : "Add a target"}
        width="lg"
      >
        {mayCommit === false ? null : plan ? (
          <CommitTarget key={target?.id ?? "no-target"} current={target} reload={() => void retryPlan()} />
        ) : null}
      </Drawer>

      <Drawer open={drawn === "assumptions"} onClose={() => setDrawn(null)} title="What it assumes" width="lg">
        {!target ? (
          <p className={cls(own.empty)} data-testid="no-baseline-to-project">
            A scenario starts from a committed baseline. A projection of no baseline is not zero, it is
            absent.
          </p>
        ) : null}
        {!plan?.assumptions.length ? (
          <p className={cls(own.empty)} data-testid="no-assumptions">
            No forecast inputs recorded.
          </p>
        ) : (
          <>
            <ul className={cls(own.assumptions)} data-testid="assumptions">
              {plan.assumptions.map((assumption) => (
                <li key={assumption.key} className={cls(own.assumption)}>
                  <span className={cls(own.assumptionKey)}>{assumption.key.replace(/_/g, " ")}</span>
                  <span className={cls(own.assumptionValue, "num")}>
                    {/* **`factorValue`, not `quantity`.** An assumption can be 0.045 or 78, and rounding to
                        a fixed number of places would either destroy the first or pad the second. */}
                    {factorValue(assumption.value)} {assumption.unit}
                  </span>
                  <span className={cls(own.assumptionSource)}>{assumption.source}</span>
                </li>
              ))}
            </ul>
            <p className={styles.note}>Recorded {shortDate(plan.assumptions_recorded_on ?? null)}.</p>
          </>
        )}
        {mayCommit === false ? (
          <p className={cls(own.unauthorized)} data-testid="not-permitted-assumption">
            An Admin or an Approver records the assumptions.
          </p>
        ) : plan ? (
          <RecordAssumption plan={plan} reload={() => void retryPlan()} />
        ) : null}
      </Drawer>

      <Drawer open={drawn === "decisions"} onClose={() => setDrawn(null)} title="Decisions" width="lg">
        {!plan?.decisions.length ? (
          <p className={cls(own.empty)} data-testid="no-decisions">
            Nothing logged.
          </p>
        ) : (
          <ul className={cls(own.decisions)} data-testid="decisions">
            {plan.decisions.map((decision) => (
              <li key={decision.id} className={cls(own.decision)}>
                <span className={cls(own.decisionWhen)}>{shortDate(decision.decided_on)}</span>
                <span className={cls(own.decisionWhat)}>{decision.summary}</span>
                <span className={cls(own.decisionWhy)}>{decision.rationale}</span>
              </li>
            ))}
          </ul>
        )}
        <p className={styles.note}>
          <HowDrawer infoKey="targets.decisions" label="Why this log cannot be edited" />
        </p>
        {mayCommit === false ? (
          <p className={cls(own.unauthorized)} data-testid="not-permitted-decision">
            An Admin or an Approver logs a decision.
          </p>
        ) : (
          <AddDecision reload={() => void retryPlan()} />
        )}
      </Drawer>
    </>
  );
}

function InitiativesTab() {
  const screen = useScreen();
  const { data: me } = useMe();
  const { data: plan, error: planFailed, refetch: retryPlan, isPending } = usePlan();
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTERS);
  const mayDeliver = me ? MAY_DELIVER.includes(me.role) : undefined;

  const rows = (plan?.initiatives ?? []).filter((row) => {
    const needle = filter.search.trim().toLowerCase();
    return !needle || row.name.toLowerCase().includes(needle);
  });
  const { sorted, sort, setSort } = useGridSort(rows, { name: (row) => row.name });

  const columns: GridColumn<PlanInitiative>[] = [
    { id: "name", header: "Initiative", sortable: true, cell: (row) => row.name },
    {
      id: "expected",
      header: "Expected",
      numeric: true,
      cell: (row) => (row.expected ? `${factorValue(row.expected)} ${row.expected_unit ?? ""}` : "Not estimated"),
    },
    {
      id: "achieved",
      header: "Achieved",
      numeric: true,
      cell: (row) => (row.achieved ? `${factorValue(row.achieved)} ${row.achieved_unit ?? ""}` : "Not yet"),
    },
    {
      id: "evidence",
      header: "Evidence",
      cell: (row) =>
        row.evidence_document_ids.length > 0
          ? `${String(row.evidence_document_ids.length)} document${row.evidence_document_ids.length === 1 ? "" : "s"}`
          : "None yet",
    },
    {
      id: "record",
      header: "Record",
      cell: (row) => <RecordLink recordId={row.id} kind="initiative" subject={row.name} />,
    },
  ];

  if (planFailed) {
    return (
      <TablePage
        icon={Leaf}
        title="Targets and plan"
        label="Initiatives"
        state={<ReadFailed of="The transition plan" error={planFailed} onRetry={() => void retryPlan()} />}
      />
    );
  }

  return (
    <>
      <TablePage
        icon={Leaf}
        title="Targets and plan"
        label="Initiatives"
        actions={
          mayDeliver === false ? (
            <span data-testid="not-permitted-initiative">
              <StatusChip tone="neutral" size="sm">
                An auditor reads the plan rather than adding to it
              </StatusChip>
            </span>
          ) : (
            <button
              type="button"
              className={styles.primary}
              onClick={() => setAdding(true)}
              data-testid="open-add-initiative"
            >
              <Plus size={14} weight="bold" aria-hidden="true" /> Add initiative
            </button>
          )
        }
        tiles={
          <TileStrip label="Initiatives at a glance">
            <Tile
              value={plan ? String(plan.initiatives.length) : "-"}
              label="Initiatives recorded"
              infoKey="targets.initiatives"
              loading={isPending}
              testId="plan-initiatives"
            />
            <Tile value={plan ? String(plan.findings.length) : "-"} label="Overlap findings" loading={isPending}
              testId="plan-findings-count" />
            <Tile value={plan ? String(plan.decisions.length) : "-"} label="Decisions logged" loading={isPending}
              testId="plan-decisions-count" />
          </TileStrip>
        }
        nothingYet={plan !== undefined && plan.initiatives.length === 0}
        chart={plan ? <InitiativeBars initiatives={plan.initiatives} search={screen} /> : undefined}
        filters={
          plan && plan.initiatives.length === 0 ? undefined : (
            <FilterLine
              filters={[]}
              state={filter}
              onChange={setFilter}
              resultCount={plan ? { shown: rows.length, total: plan.initiatives.length, noun: "initiatives" } : undefined}
            />
          )
        }
        table={
          <>
            <GridTable
              caption="Delivery actions, expected beside achieved"
              columns={columns}
              rows={sorted}
              rowKey={(row) => row.id}
              rowNumbers={false}
              loading={isPending}
              sort={sort}
              onSortChange={setSort}
              empty={
                <EmptyState
                  icon={Leaf}
                  title="No initiatives yet"
                  compact
                  testId="no-initiatives"
                  action={
                    <EmptyAction
                      label="Add initiative"
                      allowed={mayDeliver}
                      reason="An Admin, Approver or Contributor adds initiatives."
                      onClick={() => setAdding(true)}
                      testId="no-initiatives-add"
                    />
                  }
                >
                  An initiative is an action that cuts emissions.
                </EmptyState>
              }
              testId="initiatives"
            />
            {/* **Findings, not a total** (ACT-02). The server asks `portfolio.Portfolio`; this prints what
                it said. A sum would be the defect. */}
            {plan?.findings.length ? (
              <div className={cls(own.findings)} data-testid="plan-findings">
                {plan.findings.map((finding) => (
                  <p key={finding} className={cls(own.finding)}>
                    {finding}
                  </p>
                ))}
              </div>
            ) : null}
          </>
        }
      />
      <Drawer open={adding} onClose={() => setAdding(false)} title="Add an initiative" width="lg">
        {mayDeliver === false ? null : <AddInitiative reload={() => void retryPlan()} />}
      </Drawer>
    </>
  );
}

function AddInitiative({ reload }: { reload: () => void }) {
  const add = useAddInitiative();
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState("tCO2e");
  const [refusal, setRefusal] = useState<unknown>(null);

  async function submit() {
    setRefusal(null);
    try {
      await add.mutateAsync({
        name,
        expected_amount: amount ? amount : null,
        expected_unit: amount ? unit : null,
      });
      setName("");
      setAmount("");
    } catch (error) {
      setRefusal(error);
    }
  }

  return (
    <div className={cls(own.form)}>
      <label className={styles.field}>
        <span>What will you do?</span>
        <input
          aria-label="What will you do?"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="LED relamping across all sites"
        />
      </label>
      <label className={styles.field}>
        <span>What do you expect it to save?</span>
        <span className={cls(own.inline)}>
          <input
            aria-label="What do you expect it to save?"
            value={amount}
            inputMode="decimal"
            onChange={(event) => setAmount(event.target.value)}
            placeholder="180"
          />
          <input aria-label="In what unit?" value={unit} onChange={(event) => setUnit(event.target.value)} />
        </span>
      </label>
      <button
        type="button"
        className={styles.primary}
        disabled={!name.trim() || add.isPending}
        onClick={() => void submit()}
        data-testid="add-initiative"
      >
        {add.isPending ? "Adding…" : "Add it"}
      </button>
      {/* Cancel, once there is something to cancel. An initiative half-typed and abandoned must not be
          waiting the next time somebody opens the plan. */}
      {name || amount ? (
        <button
          type="button"
          className={styles.fixLink}
          data-testid="cancel-initiative"
          onClick={() => {
            setName("");
            setAmount("");
            setUnit("tCO2e");
            setRefusal(null);
          }}
        >
          Cancel
        </button>
      ) : null}
      <WriteFailed error={refusal} of="the plan" onReload={reload} />
    </div>
  );
}

function AddDecision({ reload }: { reload: () => void }) {
  const record = useRecordDecision();
  const [summary, setSummary] = useState("");
  const [rationale, setRationale] = useState("");
  const [on, setOn] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  async function submit() {
    setRefusal(null);
    try {
      await record.mutateAsync({ decided_on: on, summary, rationale });
      setSummary("");
      setRationale("");
    } catch (error) {
      setRefusal(error);
    }
  }

  return (
    <div className={cls(own.form)}>
      <label className={styles.field}>
        <span>What was decided?</span>
        <input
          aria-label="What was decided?"
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          placeholder="Deferred on-site solar"
        />
      </label>
      <label className={styles.field}>
        <span>Why?</span>
        <textarea
          aria-label="Why?"
          rows={2}
          value={rationale}
          onChange={(event) => setRationale(event.target.value)}
          placeholder="The roof survey will not be complete before the capital round closes."
        />
      </label>
      <label className={styles.field}>
        <span>When?</span>
        <input
          aria-label="When was it decided?"
          type="date"
          value={on}
          onChange={(event) => setOn(event.target.value)}
        />
      </label>
      <button
        type="button"
        className={styles.primary}
        disabled={!summary.trim() || !rationale.trim() || !on || record.isPending}
        onClick={() => void submit()}
        data-testid="add-decision"
      >
        {record.isPending ? "Logging…" : "Log it"}
      </button>
      {/* **A decision log is append-only server-side**, so a request whose outcome is unknown must not
          be re-sent on a hunch - the same decision logged twice is a record that reads as two. */}
      {summary || rationale || on ? (
        <button
          type="button"
          className={styles.fixLink}
          data-testid="cancel-decision"
          onClick={() => {
            setSummary("");
            setRationale("");
            setOn("");
            setRefusal(null);
          }}
        >
          Cancel
        </button>
      ) : null}
      <WriteFailed error={refusal} of="the decision log" onReload={reload} />
    </div>
  );
}

function TransitionScreenBody() {
  const screen = useScreen();
  const [tab] = useUrlTab(TABS);
  return (
    <ScreenFrame screen={screen} title="Targets and plan" crumb="what we have committed to, and what we are doing about it">
      {tab === "initiatives" ? <InitiativesTab /> : tab === "recommendations" ? <RecommendationsTab screen={screen} /> : <TargetsTab />}
    </ScreenFrame>
  );
}

/** Wrapped, so an unauthenticated visitor gets the sign-in form rather than a screen whose every
 * query 401s. */
export function TransitionScreen() {
  return (
    <RequireSession>
      <TransitionScreenBody />
    </RequireSession>
  );
}
